/*

  DateRange Class - Stores a start and end date, creating a range.

  The date/time JS library of choice can be implemented and abstracted away here.
  Methods provide utilities for finding the interval length of the range, and other useful
  functions.

  JS DateTime Library: Luxon
  https://moment.github.io/luxon/

 */

import { DateTime, Interval } from 'luxon';

export default class DateRange {

  constructor(start, end) {
    // create Luxon DateTime from start and end arguements and fallback to current time
    let startDateTime = start ? DateTime.fromISO(start) : DateTime.local();
    let endDateTime = end ? DateTime.fromISO(end) : DateTime.local();

    // set times to respective start or end to include the entire day(s)
    startDateTime = startDateTime.startOf('day');
    endDateTime = endDateTime.endOf('day');

    // Validation
    if (!startDateTime.isValid) {
      throw new Error('DateRange object was not passed a valid starting ISO date/time.');
    }

    if (!endDateTime.isValid) {
      throw new Error('DateRange object was not passed a valid ending ISO date/time.');
    }

    // Create range as Luxon Interval
    this.interval = Interval.fromDateTimes(startDateTime, endDateTime);
  }

  get numOfDays() {
    return this.interval.length('days');
  }

  setToday() {
    this.interval = this.interval.set({
      start: DateTime.local().startOf('day'),
      end: DateTime.local(),
    });
  }

  setFromIso({ start, end }) {
    const givenEnd = DateTime.fromISO(end).endOf('day');
    this.interval = this.interval.set({
      start: DateTime.fromISO(start).startOf('day'),
      end: givenEnd > DateTime.local() ? DateTime.local() : givenEnd,
    });
  }


  // getters and setters of DateTime objects

  get start() {
    return this.interval.start;
  }

  set start(val) {
    this.interval = this.interval.set({
      start: val,
    });
  }

  get end() {
    return this.interval.end;
  }

  set end(val) {
    this.interval = this.interval.set({
      end: val,
    });
  }


  // getters and setters of DateTime ISO objects

  get startIso() {
    return this.interval.start.toISODate();
  }

  set startIso(val) {
    this.interval = this.interval.set({
      start: DateTime.fromISO(val).startOf('day'),
    });
  }

  get endIso() {
    return this.interval.end.toISODate();
  }

  set endIso(val) {
    this.interval = this.interval.set({
      end: DateTime.fromISO(val).endOf('day'),
    });
  }

  // utility methods
  static dateExtendsPastToday(dateTime) {
    return dateTime > DateTime.local().endOf('day');
  }

}



// WEBPACK FOOTER //
// ./src/models/DateRange.js